import { computed } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import * as PleasedApi from '@/common/api/api.pleased';
import { useMockedFlowStore } from '@/library/cf-v1/mocked-flow';
import { storeToRefs } from 'pinia';
import type { UseConnectQueryOptions } from './connectQuery.types';

export interface WaitTimes {
  chat: string | null;
  call: string | null;
}

/**
 * TanStack Query hook to fetch wait times for chat and call
 *
 * @returns TanStack Query result with wait times
 *
 * @example
 * ```ts
 * const { data: waitTimes, isLoading } = useWaitTimes();
 * const chatWaitTime = computed(() => waitTimes.value?.chat);
 * ```
 */
export const useWaitTimes = (options?: UseConnectQueryOptions) => {
  const mockedFlowStore = useMockedFlowStore();
  const { isActive: isMockedFlowActive, hasConnectData, connectData } = storeToRefs(mockedFlowStore);
  const enabled = computed(() => options?.enabled?.value ?? true);

  return useQuery({
    queryKey: ['waitTimes'],
    queryFn: async (): Promise<WaitTimes> => {
      // Return mocked data if available
      if (isMockedFlowActive.value) {
        if (hasConnectData.value && connectData.value) {
          return connectData.value.waitTimes;
        }

        return {
          chat: null,
          call: null,
        };
      }

      const response = await PleasedApi.getWaitTimes().catch(() => {
        return {
          averageChatWaitingTime: null,
          averageCallWaitingTime: null,
        };
      });

      return {
        chat: response?.averageChatWaitingTime || null,
        call: response?.averageCallWaitingTime || null,
      };
    },
    enabled,
    staleTime: 30 * 1000, // Consider data fresh for 30 seconds
    refetchInterval: 60 * 1000, // Refetch every 60 seconds
    retry: 2,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10_000),
  });
};
